import { useEffect, useRef, useState } from 'react';

// Actions
import { setSelectedHistoricalScenarios } from '@/redux/modules/comparison';

// Libraries
import { Dispatch } from '@/redux';
import { useDispatch, useSelector } from 'react-redux';
import { VALUE_ESCAPE } from 'keycode-js';

// Hooks
import { useGetAvailableScenariosQuery } from '@/redux/api';

// Selectors
import { selectActiveComparison } from '@/redux/modules/comparison/selectors';

// Types
import HistoricalScenario from '@/types/historical-scenario';

export const useHistoricalScenariosFilter = () => {
  const dispatch = useDispatch<Dispatch>();

  const containerRef = useRef<HTMLDivElement>(null);
  const filterButtonRef = useRef<HTMLButtonElement>(null);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [search, setSearch] = useState<string>('');

  const activeComparison = useSelector(selectActiveComparison);

  const { data: historicalScenariosData } = useGetAvailableScenariosQuery();

  const { predefinedScenarios = [], customScenarios = [], availableScenarios = [] } = historicalScenariosData || {};
  const selectedHistoricalScenarios = activeComparison?.selectedHistoricalScenarios || [];

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleClickOutside = (event: Event) => {
    if (!(event.target instanceof Node) || !containerRef.current) return;
    if (!containerRef.current.contains(event.target)) {
      resetFilter();
    }
  };

  const handleEscapeKey = (e: React.KeyboardEvent) => {
    // Required for a11y https://www.w3.org/WAI/ARIA/apg/patterns/combobox/#listbox-popup-keyboard-interaction
    if (e.key === VALUE_ESCAPE) {
      e.nativeEvent.stopImmediatePropagation();
      handleOnToggleFilter();
      setSearch('');
    }
  };

  const handleOnToggleFilter = () => {
    setIsOpen(!isOpen);
    filterButtonRef.current?.focus();
  };

  const onScenarioChange = (scenarioId: string) => {
    const selectedScenario = availableScenarios.find((aS: HistoricalScenario) => aS._id === scenarioId);
    if (selectedScenario) {
      dispatch(setSelectedHistoricalScenarios(selectedScenario));
    }
  };

  const resetFilter = () => {
    setIsOpen(false);
    setSearch('');
  };

  return {
    containerRef,
    customScenarios,
    filterButtonRef,
    handleEscapeKey,
    handleOnToggleFilter,
    isOpen,
    onScenarioChange,
    resetFilter,
    predefinedScenarios,
    search,
    selectedHistoricalScenarios,
    setSearch,
  };
};
